// src/components/Triage.jsx
//
// Fault triage. The logic and every mapping live in src/lib/triage.js so they
// can be reviewed in one place.
//
// The point is the button at the end. Someone whose cold room is climbing at
// six in the evening arrives from a search, and this turns a panicked call
// into a report the office can dispatch against - equipment, symptom, how long
// and an urgency band, all attached to the enquiry.
//
// It never tells anyone to open a unit or touch refrigerant, and it never
// talks anyone down from calling.

import React, { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "motion/react";
import { AlertTriangle, ArrowRight, Phone, Wrench } from "lucide-react";
import { EQUIPMENT, SYMPTOMS, DURATIONS, triage, toReport } from "../lib/triage";
import { SITE } from "../content";

const TONE = {
  critical: {
    ring: "border-brooke-red",
    chip: "bg-brooke-red text-white",
    accent: "text-brooke-red",
  },
  high: {
    ring: "border-amber-500/70",
    chip: "bg-amber-500 text-black",
    accent: "text-amber-500",
  },
  medium: {
    ring: "border-line",
    chip: "bg-led/15 text-led",
    accent: "text-led",
  },
};

export default function Triage({ onUseReport }) {
  const wrapRef = useRef(null);
  const seen = useInView(wrapRef, { once: true, margin: "-70px" });

  const [equipment, setEquipment] = useState("cold-room");
  const [symptom, setSymptom] = useState("not-cooling");
  const [duration, setDuration] = useState("today");

  const result = useMemo(
    () => triage({ equipment, symptom, duration }),
    [equipment, symptom, duration]
  );

  const tone = TONE[result.band.tone];

  const send = () => {
    onUseReport?.({
      subjectRef: "breakdown",
      message: toReport(result),
    });
    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="fault" className="border-t border-line py-20 sm:py-28">
      <div ref={wrapRef} className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={seen ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brooke-red">
            Something wrong
          </p>
          <h2 className="mt-3 max-w-[18ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            Work out how urgent it is
          </h2>
          <p className="mt-6 max-w-[58ch] text-[15px] leading-relaxed text-muted sm:text-base">
            Tell us what the unit is doing and this gives you the likely causes
            and how quickly it needs attention. It also writes the report, so
            the office knows what is coming before the technician arrives.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_1fr] lg:gap-8">
          {/* --- inputs --------------------------------------------------- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={seen ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-line bg-surface2 p-6 sm:p-8"
          >
            <Group label="What is the equipment">
              <div className="grid gap-2.5">
                {EQUIPMENT.map((e) => (
                  <Choice
                    key={e.id}
                    active={equipment === e.id}
                    onClick={() => setEquipment(e.id)}
                    title={e.label}
                  />
                ))}
              </div>
            </Group>

            <Group label="What is it doing">
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {SYMPTOMS.map((s) => (
                  <Choice
                    key={s.id}
                    active={symptom === s.id}
                    onClick={() => setSymptom(s.id)}
                    title={s.label}
                    sub={s.blurb}
                  />
                ))}
              </div>
            </Group>

            <Group label="Since when">
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {DURATIONS.map((d) => (
                  <Choice
                    key={d.id}
                    active={duration === d.id}
                    onClick={() => setDuration(d.id)}
                    title={d.label}
                    compact
                  />
                ))}
              </div>
            </Group>
          </motion.div>

          {/* --- result --------------------------------------------------- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={seen ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className={`bk-plate flex flex-col rounded-2xl border-2 p-6 shadow-[0_24px_60px_-34px_rgba(4,12,20,0.5)] transition-colors sm:p-8 ${tone.ring}`}
          >
            <div className="flex items-start justify-between gap-4 border-b border-line pb-5">
              <div>
                <p className="font-mono text-[8.5px] uppercase tracking-[0.24em] text-subtle">
                  Indicated urgency
                </p>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.p
                    key={result.band.key}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    className="mt-2 font-arch text-3xl font-extrabold uppercase leading-none tracking-tight text-ink sm:text-4xl"
                  >
                    {result.band.label}
                  </motion.p>
                </AnimatePresence>
              </div>

              <span
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] ${tone.chip}`}
              >
                {result.band.tone === "medium" ? (
                  <Wrench size={11} />
                ) : (
                  <AlertTriangle size={11} />
                )}
                {result.symptom.label}
              </span>
            </div>

            <div className="space-y-3 py-6">
              <p className="text-[14.5px] leading-relaxed text-muted">
                {result.band.blurb}
              </p>
              <p className="text-[13px] leading-relaxed text-subtle">
                {result.reason}
              </p>
            </div>

            <GroupLabel>
              Likely causes on a {result.equipment.label.toLowerCase()}
            </GroupLabel>
            <ul>
              {result.causes.map((c, i) => (
                <li
                  key={c}
                  className="flex gap-3 border-b border-line py-2.5 text-[13.5px] leading-relaxed text-muted"
                >
                  <span
                    className={`mt-2 h-px w-3.5 shrink-0 bg-current ${
                      i < result.specificCount ? tone.accent : "text-subtle"
                    }`}
                  />
                  {c}
                </li>
              ))}
            </ul>

            <GroupLabel>While you wait</GroupLabel>
            <ul>
              {result.symptom.checks.map((c) => (
                <li
                  key={c}
                  className="flex gap-3 border-b border-line py-2.5 text-[13.5px] leading-relaxed text-muted"
                >
                  <span className="mt-2 h-px w-3.5 shrink-0 bg-subtle" />
                  {c}
                </li>
              ))}
            </ul>

            <p className="mt-6 rounded-lg border border-line bg-bg/50 px-4 py-3 text-[12px] leading-relaxed text-subtle">
              An indication only, based on what the unit is doing. The fault is
              confirmed on site. Do not open a sealed system or attempt work on
              refrigerant.
            </p>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <a
                href={`tel:${SITE.phone}`}
                className="inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full border border-line px-6 font-mono text-[10px] uppercase tracking-[0.16em] text-ink transition hover:border-brooke-red"
              >
                <Phone size={14} />
                {SITE.phoneLabel}
              </a>
              <button
                type="button"
                onClick={send}
                className="group inline-flex min-h-[52px] flex-[1.3] items-center justify-center gap-2.5 rounded-full bg-brooke-red px-6 font-mono text-[10px] uppercase tracking-[0.16em] text-white shadow-[0_10px_28px_-10px_rgba(231,0,26,0.8)] transition hover:bg-brooke-redDark"
              >
                Send this report
                <ArrowRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function Group({ label, children }) {
  return (
    <div className="mb-7 last:mb-0">
      <span className="mb-3 block font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
        {label}
      </span>
      {children}
    </div>
  );
}

function GroupLabel({ children }) {
  return (
    <p className="pb-2 pt-4 font-mono text-[8.5px] uppercase tracking-[0.24em] text-subtle">
      {children}
    </p>
  );
}

function Choice({ active, onClick, title, sub, compact }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-lg border text-left transition ${
        compact ? "px-3 py-2.5" : "px-4 py-3"
      } ${
        active
          ? "border-brooke-red bg-brooke-red/[0.07] ring-1 ring-brooke-red"
          : "border-line hover:border-brooke-red/50 hover:bg-brooke-red/[0.03]"
      }`}
    >
      <span
        className={`block font-arch font-extrabold text-ink ${
          compact ? "text-[12.5px]" : "text-[13.5px]"
        }`}
      >
        {title}
      </span>
      {sub && (
        <span className="mt-1 block text-[12px] leading-snug text-subtle">
          {sub}
        </span>
      )}
    </button>
  );
}